// eslint-disable-next-line max-classes-per-file
import { OhsCusval } from 'global-components/form/cusvals/OhsCusvalModel';
import { OhsAttachment } from 'global-components/form/fields/attachments/OhsAttachmentModels';
import { OhsSignature } from 'global-components/form/fields/signature/OhsSignatureModels';
import { ModuleType } from 'global-services/constants/OhsObject';
import { OhsTier } from 'global-services/constants/tier/OhsTier';
import { getCurrentTimezone } from 'global-services/OhsDataParse';

export interface OhsInjuryMgmtTemplateListPayload {
  _id?: string;
  forType: ModuleType.Injury;
  category?: string;
  categoryOther?: string;
  filter: {
    viewPreset?: 'view_1' | 'view_2' | 'view_3' | 'view_4';
    page: number;
    sort: {
      order: number;
      key: string;
    };
    count: boolean;
    archived?: boolean;
    workplaces?: string[];
    categories?: string[];
    subcategories?: string[];
    templateTypes?: string[];
    allocatedToWorkplace?: boolean;
    notAllocated?: boolean;
    workplaceOwned?: boolean;
    skipMaster?: boolean;
  };
}

export enum OhsInjmtTaskFrequencyType {
  OneOff = 'null',
  Recurring = 'FREQUENCY',
  UserDefined = 'USER_DATE_DUE',
}

export interface OhsInjuryMgmtPayload {
  viewPreset?: 'view_1' | 'view_2' | 'view_3' | 'view_4';
  page: number;
  sort: {
    order: number;
    key: string;
  };
  count: boolean;

  archived?: boolean;

  workplaces?: string[];
  categories?: string[];
  subcategories?: string[];
  templateTypes?: string[];
  allocatedToWorkplace?: boolean;
  notAllocated?: boolean;
  workplaceOwned?: boolean;
}

export interface SysField {
  _id: string;
  type: string;
  name: string;
  fieldType: string;
  required: boolean;
  value?: any;
}

interface ColumnControl {
  _id: string;
}

export class OhsWmmTemplateTaskRecord {
  allowFrequencyEdit: boolean;

  allowStopRecurrence: boolean;

  createImmediately: boolean;

  frequencyValue: number | undefined;

  frequencyKey: string | undefined;

  description: string;

  enableAttachments: number;

  enableSignatures: number;

  enabled: boolean;

  recurrence: {
    mode: string;
  } | null;

  title: string;

  sysfields: SysField[];

  cusvals: OhsCusval[];

  layout: { title: string; items: string[] }[];

  constructor() {
    this.allowFrequencyEdit = false;
    this.allowStopRecurrence = false;
    this.createImmediately = false;

    this.frequencyValue = undefined;
    this.frequencyKey = undefined;
    this.description = '';

    this.enableAttachments = 0;
    this.enableSignatures = 0;

    this.enabled = false;

    this.recurrence = {
      mode: '',
    };

    this.title = '';

    this.sysfields = [];

    this.cusvals = [];

    this.layout = [{ title: '', items: [] }];
  }
}

export class OhsInjuryMgmtTemplateRecord {
  _id: string;

  public static readonly type: ModuleType.InjuryTemplate;

  forType?: string;

  master: boolean;

  tzDateCreated: string;

  title: string;

  description: string;

  record: {
    sysfields: SysField[];
    cusvals: OhsCusval[];
    task?: OhsWmmTemplateTaskRecord;
    enableAttachments: boolean | number;
    enableSignatures: boolean | number;
    allowReference?: boolean;
    layout: {
      title: string;
      items: string[];
    }[];
  };

  dateCreated?: string;

  details?: OhsInjuryMgmtTemplateRecord;

  tier?: OhsTier;

  category: string;

  categoryOther: string;

  subcategory: string;

  subcategoryOther: string;

  columnControl: ColumnControl[];

  subRows?: Array<OhsInjuryMgmtTemplateRecord>;

  allocationOf?: { _id: string; type: ModuleType.InjuryTemplate } | undefined;

  constructor() {
    this._id = '';
    this.forType = ModuleType.Injury;
    this.master = false;
    this.tzDateCreated = getCurrentTimezone();
    this.title = '';
    this.description = '';
    this.record = {
      sysfields: [],
      cusvals: [],

      layout: [{ title: '', items: [] }],
      enableAttachments: false,
      enableSignatures: false,
      task: new OhsWmmTemplateTaskRecord(),
    };
    this.category = '';
    this.categoryOther = '';
    this.subcategory = '';
    this.subcategoryOther = '';
    this.columnControl = [];
  }
}

export default class OhsInjuryMgmtRecord {
  _id: string;

  public static readonly type = ModuleType.Injury;

  title: string;

  fieldType: string;

  description?: string;

  options: {
    listItems: { title: string }[];
  };

  option?: string;

  dateCreated: string;

  dateDue?: string;

  tzDateCreated: string;

  frequencyKey: string;

  frequencyValue: number;

  status: string;

  folderStatus?: string;

  tier?: OhsTier;

  createdBy: {
    type: ModuleType.User;
    _id: string;
    name: string;
    email: string;
  };

  owner?: {
    type: ModuleType.Injury;
    _id: string;
    title?: string;
  };

  template?: {
    type: ModuleType.InjuryTemplate;
    _id: string;
    details?: OhsInjuryMgmtTemplateRecord;
    title?: string;
  };

  recordTemplate?: {
    type: ModuleType.InjuryTemplate;
    _id: string;
    details?: OhsInjuryMgmtTemplateRecord;
    title?: string;
  };

  taskTemplate?: {
    type: ModuleType.InjuryTemplate;
    _id: string;
    title?: string;
  };

  archived: boolean;

  subRows: any[];

  attachments: OhsAttachment[];

  signatures: OhsSignature[];

  category: string;

  subcategory: string;

  categoryOther: string;

  subcategoryOther: string;

  reference: string;

  cusvals: any;

  sysfields: any;

  master?: boolean;

  constructor(tier?: OhsTier) {
    this._id = '';
    this.tzDateCreated = getCurrentTimezone();
    this.tier = tier;
    this.createdBy = {
      type: ModuleType.User,
      _id: '',
      name: '',
      email: '',
    };
    this.dateCreated = '';
    this.status = '';
    this.title = '';
    this.attachments = [];
    this.signatures = [];
    this.options = { listItems: [] };
    this.fieldType = '';
    this.archived = false;
    this.subRows = [];
    this.category = '';
    this.categoryOther = '';
    this.subcategory = '';
    this.subcategoryOther = '';
    this.reference = '';
    this.cusvals = null;
    this.sysfields = null;
    this.template = undefined;
    this.frequencyKey = '';
    this.frequencyValue = 0;
  }
}
